// Shared deep-link page-jump for the client-side-paginated tracking tabs.
//
// When a cross-link navigates to `?tab=<x>&item=<id>` the target row is
// highlighted, but the client-side paginator stays on its current page — so an
// item on a later page is highlighted off-screen and appears "not to show".
// This hook jumps to the page that actually contains the deep-linked row.
//
// It fires exactly once per item id (a ref keyed on the id) so it never fights
// the user's manual paging afterwards, and it waits until the item resolves to
// a real row index before jumping (the list may still be loading, or the item
// may be genuinely absent — a deleted/archived row, which each tab's existing
// "not in this list" banner handles).

import { useEffect, useRef } from "react";

/** The 1-based page that the row at `index` (0-based) falls on. */
export function pageForIndex(index: number, pageSize: number): number {
  return Math.floor(index / pageSize) + 1;
}

export interface DeepLinkPageJumpOptions {
  /** The deep-linked item id from the URL (`?item=`), or null when absent. */
  itemId: string | null | undefined;
  /** 0-based index of the row containing the item within the paginated row
   *  list, or -1 when the item is not (yet) in the list. */
  targetIndex: number;
  /** Page size used by the tab's client-side paginator. */
  pageSize: number;
  /** Apply the resolved jump: set the page (and run any side-effects, e.g.
   *  expanding the parent of a deep-linked child row). Receives the 1-based
   *  target page, the 0-based row index, and the resolved item id — passed
   *  through as a plain string so callbacks can highlight/expand the row
   *  without re-null-checking a value that is guaranteed present here. */
  onJump: (targetPage: number, index: number, itemId: string) => void;
}

export function useDeepLinkPageJump({
  itemId,
  targetIndex,
  pageSize,
  onJump,
}: DeepLinkPageJumpOptions): void {
  const handledRef = useRef<string | null>(null);
  useEffect(() => {
    // Reset once the deep-link clears so re-linking the same item re-jumps.
    if (!itemId) {
      handledRef.current = null;
      return;
    }
    if (targetIndex < 0) return; // not resolved yet — wait for the row to exist
    if (handledRef.current === itemId) return; // already jumped for this id
    handledRef.current = itemId;
    onJump(pageForIndex(targetIndex, pageSize), targetIndex, itemId);
  }, [itemId, targetIndex, pageSize, onJump]);
}
